import { useMemo } from "react";
import { SUPPORTED_LANGUAGES, BASE_LANGUAGE_CODES } from "src/i18n";
import type { LanguageCode } from "src/i18n";
import { languageFlagKey } from "src/featureFlags";
import { useFeatureFlagEnabled } from "src/hooks/useFeatureFlagEnabled";

type Language = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * The released languages: the always-on base set plus any flagged language
 * whose `lang-<code>` PostHog flag is on. Fail-closed (base-only) until flags
 * resolve.
 *
 * One unconditional hook call per flagged language keeps hook order stable
 * (rules-of-hooks). The result is memoized on the flag booleans so the returned
 * array reference only changes when an actual flag flips — consumers can use it
 * directly as a `useEffect`/context dependency without it over-firing. Adding a
 * new non-base language means adding its line here; the test iterates every
 * non-base language to catch a forgotten entry.
 */
export function useEnabledLanguages(): Language[] {
  const fr = useFeatureFlagEnabled(languageFlagKey("fr"));
  const de = useFeatureFlagEnabled(languageFlagKey("de"));
  const pt = useFeatureFlagEnabled(languageFlagKey("pt"));
  const zh = useFeatureFlagEnabled(languageFlagKey("zh"));
  const sw = useFeatureFlagEnabled(languageFlagKey("sw"));

  return useMemo(() => {
    const flagOn: Partial<Record<LanguageCode, boolean>> = { fr, de, pt, zh, sw };
    return SUPPORTED_LANGUAGES.filter((l) => BASE_LANGUAGE_CODES.includes(l.code) || flagOn[l.code] === true);
  }, [fr, de, pt, zh, sw]);
}
